// SENTINEL: 1_Telemetery - Application Root (Router, State, Vitals)
// Vitality: Healthy (Pass 10 Stabilized)
import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { IdeaManager } from './components/IdeaManager';
import { ChatInterface } from './components/ChatInterface';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastContainer, ToastMessage, ToastType } from './components/Toast.tsx';
import { AccountSwitcher } from './components/AccountSwitcher';
import { SyncStatusIndicator } from './components/SyncStatusIndicator';
import { useMultiTabSync } from './hooks/useMultiTabSync';
import { logger } from './services/loggingService';
import { accountService } from './services/accountService';
import { GUEST_ACCOUNT_ID } from './services/offlineStorageService';
import { syncStatusService } from './services/syncStatusService';

const LiveInterface = lazy(() => import('./components/LiveInterface').then(m => ({ default: m.LiveInterface })));
const LocalVoiceLiveInterface = lazy(() => import('./components/LocalVoiceLiveInterface').then(m => ({ default: m.LocalVoiceLiveInterface })));
const SettingsDialog = lazy(() => import('./components/SettingsDialog').then(m => ({ default: m.SettingsDialog })));
const DiagnosticPanel = lazy(() => import('./components/DiagnosticPanel').then(m => ({ default: m.DiagnosticPanel })));
const AIEdgeManager = lazy(() => import('./components/AIEdgeManager').then(m => ({ default: m.AIEdgeManager })));

import { DebugPanel } from './components/DebugPanel';
import { Idea, Message, MessageRole, User, Section, Attachment, ProposalChange, ExecutionPlan, GroundingSource } from './types';
import { sendMessage, sendMessageStream, UnifiedAIResponse, testProviderConnection, getActiveProviderInfo } from './services/unifiedAIService';
import { dbService } from './services/dbService';
import { config, isDebugUserAllowed } from './config';
import { getPaletteById, applyPalette, DEFAULT_PALETTE } from './theme/palette';
import { PROVIDER_INFO } from './services/providers/types';
import { Loader2, LogOut, Wifi, Moon, Sun, CloudOff, LogIn, AlertTriangle, ShieldAlert, Settings, Bug, Component, BookOpen, ToggleLeft, ToggleRight, HelpCircle, X, Sparkles, Mic, FolderSync, FileDown, Shield, Brain, Search, Lightbulb, FileText, Presentation, Table, HardDrive, Cloud, Edit3, Users, RefreshCw, Smartphone, Terminal, Zap, Server } from 'lucide-react';

// --- Global Error Handlers (chain existing handlers) ---
if (typeof window !== 'undefined') {
    const existingOnError = window.onerror;
    const existingOnUnhandledRejection = window.onunhandledrejection;

    window.onerror = (message, source, lineno, colno, error) => {
        logger.error('System', 'Uncaught error', {
            message: String(message),
            source,
            lineno,
            colno,
            stack: error?.stack
        });
        if (existingOnError) {
            return existingOnError(message, source, lineno, colno, error);
        }
        return false;
    };

    window.onunhandledrejection = (event) => {
        logger.error('System', 'Unhandled promise rejection', {
            reason: event.reason?.message || String(event.reason),
            stack: event.reason?.stack
        });
        if (existingOnUnhandledRejection) {
            existingOnUnhandledRejection.call(window, event);
        }
    };

    // Log startup config status (no PII)
    logger.info('Config', 'Application starting', {
        hasGoogleClientId: !!config.google.clientId,
        googleClientIdLength: config.google.clientId?.length || 0,
        hasGeminiApiKey: !!config.gemini.apiKey,
        geminiApiKeyLength: config.gemini.apiKey?.length || 0,
        activeProvider: config.providers.active,
        driveSyncEnabled: config.features.enableDriveSync
    });
}

const generateId = () => Math.random().toString(36).substring(2, 15);

// --- Helper: Data Integrity ---
const sanitizeSections = (sections: Section[] | undefined): Section[] => {
    if (!sections || sections.length === 0) return [];

    const validIds = new Set(sections.map(s => s.id));

    return sections.map(section => {
        // If a section has a parentId, check if that parent actually exists
        if (section.parentId) {
            if (!validIds.has(section.parentId)) {
                // Orphan found! Promote to root to make it visible
                return { ...section, parentId: null };
            }
        }
        return section;
    }).sort((a, b) => a.order - b.order);
};

const App: React.FC = () => {
    // --- Theme State ---
    const [theme, setTheme] = useState<'light' | 'dark'>('light');
    const [paletteId, setPaletteId] = useState<string>(() => {
        return localStorage.getItem('cc_color_palette') || 'ocean';
    });
    const [textScale, setTextScale] = useState<'11' | '12' | '13' | '14'>(() => {
        return (localStorage.getItem('cc_text_scale') as '11' | '12' | '13' | '14') || '12';
    });
    const [textClarity, setTextClarity] = useState<'crisp' | 'clear' | 'readable' | 'bold' | 'strong'>(() => {
        return (localStorage.getItem('cc_text_clarity') as 'crisp' | 'clear' | 'readable' | 'bold' | 'strong') || 'crisp';
    });
    const [showTileEdgeColors, setShowTileEdgeColors] = useState<boolean>(() => {
        const stored = localStorage.getItem('cc_tile_edge_colors');
        return stored !== null ? stored === 'true' : true; // Default enabled
    });
    const [uiScale, setUiScale] = useState<'67' | '75' | '80' | '90' | '100' | '110'>(() => {
        return (localStorage.getItem('cc_ui_scale') as '67' | '75' | '80' | '90' | '100' | '110') || '90';
    });

    // --- Auth State ---
    const [user, setUser] = useState<User | null>(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    // --- Network State ---
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    // --- App State ---
    const [ideas, setIdeas] = useState<Idea[]>([]);
    const [activeIdeaId, setActiveIdeaId] = useState<string | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [currentPlan, setCurrentPlan] = useState<ExecutionPlan | null>(null); // New Plan State
    const [isProcessing, setIsProcessing] = useState(false);
    const [isLiveMode, setIsLiveMode] = useState(false);
    const [isLocalVoiceMode, setIsLocalVoiceMode] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isDiagnosticOpen, setIsDiagnosticOpen] = useState(false);
    const [isArchitectMode, setIsArchitectMode] = useState(false);
    const [creativityLevel, setCreativityLevel] = useState(0.5); // 0.0 = Analytical, 1.0 = Creative // Default to Explorer Mode
    const [isHelpOpen, setIsHelpOpen] = useState(false); // Help tooltip state
    const [isChatFullscreen, setIsChatFullscreen] = useState(false); // Fullscreen chat for mobile
    const [isChatCollapsed, setIsChatCollapsed] = useState(false); // Collapse chat to see more of the app
    const [activeProvider, setActiveProvider] = useState<string>(config.providers.active);


    // --- Notification State ---
    const [toasts, setToasts] = useState<ToastMessage[]>([]);

    const showToast = (title: string, message: string, type: ToastType = 'info', forceId?: string) => {
        const id = forceId || generateId();
        setToasts(prev => {
            // Remove existing toast with same ID if it exists (forcing an update/replace)
            const filtered = prev.filter(t => t.id !== id);
            return [...filtered, { id, title, message, type }];
        });
    };

    const dismissToast = (id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    };

    const handleSettingsChangedFromOtherTab = useCallback((key: string, newValue: string | null) => {
        if (key === 'cc_color_palette' && newValue) {
            setPaletteId(newValue);
        } else if (key === 'cc_text_scale' && newValue) {
            setTextScale(newValue as '11' | '12' | '13' | '14');
        } else if (key === 'cc_text_clarity' && newValue) {
            setTextClarity(newValue as 'crisp' | 'clear' | 'readable' | 'bold' | 'strong');
        } else if (key === 'cc_ui_scale' && newValue) {
            setUiScale(newValue as any);
        } else if (key === 'cc_tile_edge_colors') {
            setShowTileEdgeColors(newValue === 'true');
        }
    }, []);

    const handleDataChangedFromOtherTab = useCallback(async () => {
        showToast('Sync', 'Data was updated in another tab. Refreshing...', 'info');
        try {
            const data = await dbService.loadData();
            const sanitizedIdeas = data.ideas.map(i => ({
                ...i,
                sections: sanitizeSections(i.sections)
            }));
            setIdeas(sanitizedIdeas);
            setMessages(data.messages);
        } catch (e) {
            showToast('Sync Error', 'Failed to reload data from other tab.', 'error');
        }
    }, []);

    const { notifyDataChange } = useMultiTabSync({
        onSettingsChanged: handleSettingsChangedFromOtherTab,
        onDataChanged: handleDataChangedFromOtherTab
    });

    // Derived State
    const activeIdea = ideas.find(i => i.id === activeIdeaId) || null;

    // --- Initialization ---
    useEffect(() => {
        // Check system preference for theme
        const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (prefersDark) {
            setTheme('dark');
        }

        // Apply initial palette
        const savedPaletteId = localStorage.getItem('cc_color_palette') || 'ocean';
        const palette = getPaletteById(savedPaletteId);
        applyPalette(palette, prefersDark);

        const handleOnline = () => { setIsOnline(true); showToast('Online', 'Connection restored.', 'success'); };
        const handleOffline = () => { setIsOnline(false); showToast('Offline', 'You are currently offline.', 'error'); };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // Initialize DB/Auth - data is passed directly from init to ensure hydration
        dbService.init(async (data, restoredUser) => {
            setIsAuthReady(true);

            // Restore user session if available
            if (restoredUser) {
                setUser(restoredUser);
                // Sync account service so UI components show the correct account
                accountService.addAccount(restoredUser);
                accountService.setActiveAccount(restoredUser.id);
                logger.info('App', 'Restored user session', { username: restoredUser.username });
            }

            // Sanitize loaded ideas immediately to fix existing bugs
            const sanitizedIdeas = data.ideas.map(i => ({
                ...i,
                sections: sanitizeSections(i.sections)
            }));
            setIdeas(sanitizedIdeas);
            setMessages(data.messages);

            // Check for backup reminder (only for guest users with projects)
            if (!restoredUser && sanitizedIdeas.length > 0) {
                try {
                    const { projectExportService } = await import('./services/projectExportService');
                    if (projectExportService.shouldSuggestBackup(7)) {
                        setTimeout(() => {
                            showToast(
                                'Backup Reminder',
                                'Your projects are saved locally. Consider creating a backup in Settings > System.',
                                'info'
                            );
                        }, 5000);
                    }
                } catch (e) {
                    // Ignore backup check errors
                }
            }
        });

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        }
    }, []);

    // Update palette when theme or paletteId changes
    useEffect(() => {
        const palette = getPaletteById(paletteId);
        applyPalette(palette, theme === 'dark');
    }, [theme, paletteId]);

    // Apply text scale on mount and when it changes
    useEffect(() => {
        document.body.classList.remove('text-scale-11', 'text-scale-12', 'text-scale-13', 'text-scale-14');
        if (textScale !== '12') {
            document.body.classList.add(`text-scale-${textScale}`);
        }
    }, [textScale]);

    // Apply UI scale
    useEffect(() => {
        document.documentElement.classList.remove(
            'app-scale-67', 'app-scale-75', 'app-scale-80',
            'app-scale-90', 'app-scale-100', 'app-scale-110'
        );
        document.documentElement.classList.add(`app-scale-${uiScale}`);
        localStorage.setItem('cc_ui_scale', uiScale);

        // Debug confirmation (remove in production)
        if (uiScale !== '90') { // Don't show on default load unless changed
            showToast('UI Scale', `Applied scaling: ${uiScale}%`, 'info');
        }
    }, [uiScale]);

    // Apply text clarity on mount and when it changes
    useEffect(() => {
        document.body.classList.remove('text-clarity-crisp', 'text-clarity-clear', 'text-clarity-readable', 'text-clarity-bold', 'text-clarity-strong');
        document.body.classList.add(`text-clarity-${textClarity}`);
    }, [textClarity]);

    const handlePaletteChange = (newPaletteId: string) => {
        setPaletteId(newPaletteId);
        localStorage.setItem('cc_color_palette', newPaletteId);
    };

    const handleTextScaleChange = (newTextScale: '11' | '12' | '13' | '14') => {
        setTextScale(newTextScale);
        localStorage.setItem('cc_text_scale', newTextScale);
    };

    const handleTextClarityChange = (newClarity: 'crisp' | 'clear' | 'readable' | 'bold' | 'strong') => {
        setTextClarity(newClarity);
        localStorage.setItem('cc_text_clarity', newClarity);
        document.body.classList.remove('text-clarity-crisp', 'text-clarity-clear', 'text-clarity-readable', 'text-clarity-bold', 'text-clarity-strong');
        document.body.classList.add(`text - clarity - ${newClarity} `);
    };

    const handleTileEdgeColorsChange = (enabled: boolean) => {
        setShowTileEdgeColors(enabled);
        localStorage.setItem('cc_tile_edge_colors', String(enabled));
    };

    const loadUserData = async (userId: string) => {
        const data = await dbService.getUserData(userId);
        // Sanitize on load
        const sanitizedIdeas = data.ideas.map(i => ({
            ...i,
            sections: sanitizeSections(i.sections)
        }));
        setIdeas(sanitizedIdeas);
        setMessages(data.messages);
    };

    const handleLogin = async () => {
        if (!isAuthReady) return;
        setIsLoggingIn(true);
        try {
            const localIdeas = [...ideas];
            const localMessages = [...messages];

            const loggedInUser = await dbService.login();

            accountService.addAccount(loggedInUser);
            accountService.setActiveAccount(loggedInUser.id);

            setUser(loggedInUser);
            await loadUserData(loggedInUser.id);

            if (localIdeas.length > 0) {
                const driveData = await dbService.getUserData(loggedInUser.id);
                const driveIdeaIds = new Set(driveData.ideas.map(i => i.id));
                const newLocalIdeas = localIdeas.filter(i => !driveIdeaIds.has(i.id));

                if (newLocalIdeas.length > 0) {
                    const shouldImport = window.confirm(
                        `You have ${newLocalIdeas.length} local project(s) that aren't in your Google Drive.\n\n` +
                        `Would you like to import them to your Drive account?\n\n` +
                        `Click OK to import, or Cancel to skip.`
                    );

                    if (shouldImport) {
                        for (const idea of newLocalIdeas) {
                            await dbService.saveIdea(loggedInUser.id, idea);
                        }
                        const localMsgsForImported = localMessages.filter(m =>
                            newLocalIdeas.some(i => i.id === m.ideaId)
                        );
                        for (const msg of localMsgsForImported) {
                            await dbService.saveMessage(loggedInUser.id, msg);
                        }
                        await loadUserData(loggedInUser.id);
                        showToast('Projects Imported', `${newLocalIdeas.length} local project(s) synced to Drive.`, 'success');
                    }
                }
            }

            showToast('Welcome', `Signed in as ${loggedInUser.username}`, 'success');
        } catch (e) {
            console.error("Login failed", e);
            showToast('Login Failed', 'Check your configuration in Settings.', 'error');
        } finally {
            setIsLoggingIn(false);
        }
    };

    const handleLogout = async () => {
        const currentAccountId = accountService.getActiveAccountId();

        await dbService.logout();
        accountService.removeAccount(currentAccountId);
        accountService.switchToGuest();

        setUser(null);
        const data = await dbService.loadData();
        setIdeas(data.ideas);
        setMessages(data.messages);
        setActiveIdeaId(null);
        setCurrentPlan(null);
        showToast('Logged Out', 'Returned to guest mode.', 'info');
    };

    const handleSwitchAccount = async (accountId: string) => {
        if (accountId === accountService.getActiveAccountId()) return;

        syncStatusService.resetForAccountSwitch();

        setIdeas([]);
        setMessages([]);
        setActiveIdeaId(null);
        setCurrentPlan(null);

        accountService.setActiveAccount(accountId);

        if (accountId === GUEST_ACCOUNT_ID) {
            setUser(null);
            try {
                const data = await dbService.loadLocalData();
                setIdeas(data.ideas);
                setMessages(data.messages);
                showToast('Switched', 'Now using Guest workspace', 'info');
            } catch (e) {
                console.error('Failed to load guest data', e);
                showToast('Error', 'Failed to load guest data', 'error');
            }
        } else {
            const account = accountService.getAccountById(accountId);
            if (account?.user) {
                setUser(account.user);
                try {
                    await loadUserData(account.user.id);
                    showToast('Switched', `Now using ${account.user.username}'s workspace`, 'info');
                } catch (e) {
                    console.error('Failed to load account data', e);
                    showToast('Error', 'Failed to load account data', 'error');
                }
            }
        }
    };

    // --- Provider Actions ---
    const handleToggleProvider = () => {
        const current = activeProvider;
        const newProvider = current === 'onnx' ? (config.providers.active === 'onnx' ? 'gemini' : config.providers.active) : 'onnx';

        // If config.active is ALREADY onnx, we need a fallback. Default to gemini or first available.
        // Actually, let's keep it simple: Toggle between "ONNX" and "Whatever was valid before".
        // If we are currently ONNX, switch to 'gemini' (or whatever is the backup). 
        // Ideally we should track "previous provider".

        let target = 'onnx';
        if (current === 'onnx') {
            // Switch BACK to main provider. 
            // If the main configured provider IS onnx, then we switch to gemini as a distinct alternative.
            if (config.providers.active === 'onnx') {
                target = 'gemini';
            } else {
                target = config.providers.active;
            }
        }

        setActiveProvider(target);

        // Optional: Persist this temporary toggle? 
        // If we want it to stick, we update config. 
        // If we only want it for this session, we don't saveConfig.
        // User requested "toggle between", implies session switching.
        // But if they refresh, they might expect it to stay.
        // Let's update the global config reference for valid API calls
        config.providers.active = target as any;
        // We WON'T call saveConfig(true) to avoid reload, but we update localStorage
        import('./config').then(({ saveConfig }) => {
            saveConfig(config, false); // false = no reload
        });

        showToast('Provider Switched', `Now using ${target === 'onnx' ? 'AI Edge (ONNX)' : target}`, 'info');
    };

    // --- Idea Actions ---
    const handleCreateIdea = async (title: string, initialContent?: string, initialSections?: Section[], initialAttachments?: Attachment[]) => {
        const newIdea: Idea = {
            id: generateId(),
            title,
            content: initialContent || '',
            sections: initialSections && initialSections.length > 0
                ? initialSections
                : [{ id: 'main', title: 'Overview', content: initialContent || '', lastUpdated: Date.now(), order: 0, parentId: null }],
            attachments: initialAttachments || [],
            createdAt: Date.now(),
            updatedAt: Date.now(),
        };

        setIdeas([...ideas, newIdea]);
        setActiveIdeaId(newIdea.id);
        setIsArchitectMode(true); // Always enable Architect for new projects
        setCurrentPlan(null); // Reset plan on new idea
        await dbService.saveIdea(user ? user.id : 'guest', newIdea);
        showToast('Project Created', `"${title}" is ready.`, 'success');
    };

    const handleUpdateIdea = async (id: string, content: string, sections?: Section[], attachments?: Attachment[], title?: string, googleDocId?: string, googleFolderId?: string) => {
        // Sanitize sections before saving to prevent orphans
        const cleanSections = sanitizeSections(sections);

        const updatedIdeas = ideas.map(idea =>
            idea.id === id ? {
                ...idea,
                content,
                sections: cleanSections,
                attachments,
                title: title || idea.title, // Update title if provided
                googleDocId: googleDocId || idea.googleDocId,
                googleFolderId: googleFolderId || idea.googleFolderId,
                updatedAt: Date.now()
            } : idea
        );
        setIdeas(updatedIdeas);

        const updatedIdea = updatedIdeas.find(i => i.id === id);
        if (updatedIdea) {
            await dbService.saveIdea(user ? user.id : 'guest', updatedIdea);
        }
    };

    const handleDeleteIdea = async (id: string) => {
        setIdeas(prev => prev.filter(i => i.id !== id));
        if (activeIdeaId === id) setActiveIdeaId(null);
        setCurrentPlan(null);
        await dbService.deleteIdea(user ? user.id : 'guest', id);
        showToast('Deleted', 'Project removed successfully.', 'info');
    };

    const handleSelectIdea = (id: string | null) => {
        setActiveIdeaId(id);
        if (id) {
            const selectedIdea = ideas.find(i => i.id === id);
            setCurrentPlan(selectedIdea?.plan || null);
        } else {
            setCurrentPlan(null);
        }
    };

    const handleClearChat = async () => {
        if (!activeIdeaId && !activeIdea) {
            setMessages([]);
            return;
        }

        if (activeIdeaId) {
            const updatedIdeas = ideas.map(idea =>
                idea.id === activeIdeaId ? { ...idea, messages: [], updatedAt: Date.now() } : idea
            );
            setIdeas(updatedIdeas);
            const updatedIdea = updatedIdeas.find(i => i.id === activeIdeaId);
            if (updatedIdea) {
                await dbService.saveIdea(user ? user.id : 'guest', updatedIdea);
            }
        }
        setMessages([]);
        showToast('Cleared', 'Chat history cleared.', 'info');
    };

    const handleDeleteMessage = async (msgId: string) => {
        // Optimistic UI update
        const updatedMessages = messages.filter(m => m.id !== msgId);
        setMessages(updatedMessages);

        if (activeIdeaId) {
            // BUGFIX: Only save messages belonging to this idea, not valid global state
            const projectMessages = updatedMessages.filter(m => m.ideaId === activeIdeaId);

            const updatedIdeas = ideas.map(idea =>
                idea.id === activeIdeaId ? { ...idea, messages: projectMessages, updatedAt: Date.now() } : idea
            );
            setIdeas(updatedIdeas);
            const updatedIdea = updatedIdeas.find(i => i.id === activeIdeaId);
            if (updatedIdea) {
                await dbService.saveIdea(user ? user.id : 'guest', updatedIdea);
                // Also explicitly delete from DB to ensure sync
                if (user) {
                    await dbService.deleteMessage(user.id, msgId);
                } else {
                    await dbService.deleteMessage('guest', msgId);
                }
            }
        } else {
            // General chat deletion
            const currentUserId = user ? user.id : 'guest';
            await dbService.deleteMessage(currentUserId, msgId);
        }
        showToast('Deleted', 'Message removed.', 'info', 'delete-message-toast');
    };

    const savePlanToIdea = async (plan: ExecutionPlan | null) => {
        if (!activeIdeaId) return;
        const currentUserId = user ? user.id : 'guest';
        const updatedIdeas = ideas.map(idea =>
            idea.id === activeIdeaId ? { ...idea, plan, updatedAt: Date.now() } : idea
        );
        setIdeas(updatedIdeas);
        const updatedIdea = updatedIdeas.find(i => i.id === activeIdeaId);
        if (updatedIdea) {
            await dbService.saveIdea(currentUserId, updatedIdea);
        }
    };

    // --- Chat Actions ---
    const handleSendMessage = async (text: string) => {
        const currentUserId = user ? user.id : 'guest';

        // Add user message immediately
        const userMsg: Message = {
            id: generateId(),
            role: MessageRole.USER,
            text,
            timestamp: Date.now(),
            ideaId: activeIdeaId
        };

        const currentMessages = [...messages, userMsg];
        setMessages(currentMessages);
        setIsProcessing(true);

        // Save USER message to DB
        await dbService.saveMessage(currentUserId, userMsg);

        // Initial placeholder message for AI
        const loadingId = generateId();
        const loadingMsg: Message = {
            id: loadingId,
            role: MessageRole.MODEL,
            text: '',
            timestamp: Date.now(),
            ideaId: activeIdeaId,
            isLoading: true
        };

        setMessages(prev => [...prev, loadingMsg]);

        try {
            const contextMessages = currentMessages.filter(m =>
                activeIdeaId ? m.ideaId === activeIdeaId : m.ideaId === null
            );

            // PASS THE MODE
            const stream = sendMessageStream(contextMessages, text, activeIdea, ideas, isArchitectMode, creativityLevel);

            let fullText = '';
            let finalResponseData: UnifiedAIResponse | undefined;

            for await (const chunk of stream) {
                if (chunk.isComplete && chunk.finalResponse) {
                    finalResponseData = chunk.finalResponse;
                    break;
                }

                if (chunk.text) {
                    fullText += chunk.text;
                    setMessages(prev => prev.map(msg =>
                        msg.id === loadingId
                            ? { ...msg, text: fullText, isLoading: false }
                            : msg
                    ));
                }
            }

            if (!finalResponseData) {
                // Fallback if stream ended without final packet (shouldn't happen)
                finalResponseData = { text: fullText };
            }

            // Handle Plan Updates from AI
            if (finalResponseData.planUpdate) {
                const newPlan = {
                    title: finalResponseData.planUpdate.title,
                    steps: finalResponseData.planUpdate.steps,
                    isActive: true
                };
                setCurrentPlan(newPlan);
                savePlanToIdea(newPlan);
            }

            // Fallback for empty text responses (common when AI uses tools only)
            let responseText = finalResponseData.text;
            if (!responseText) {
                if (finalResponseData.planUpdate) {
                    responseText = "**Execution Plan Updated.** Please review the objectives in the sidebar.";
                } else if (finalResponseData.proposal) {
                    responseText = "**New Changes Proposed.** I have drafted some updates for you to review above.";
                } else {
                    // responseText = "I've processed your request.";
                    // Don't show generic filler. If model failed to output text, it might just have tool calls or internal thought.
                    // If truly empty, let UI handle it or show nothing.
                    responseText = "";
                }
            }

            const finalMsg: Message = {
                id: loadingId,
                role: MessageRole.MODEL,
                text: responseText,
                timestamp: Date.now(),
                ideaId: activeIdeaId,
                isLoading: false,
                groundingSources: finalResponseData.groundingSources,
                isPlanUpdate: !!finalResponseData.planUpdate,
                suggestedActions: finalResponseData.suggestedActions, // Map suggested actions
                fallbackModel: finalResponseData.modelUsed, // If fallback model was used in Explorer mode
                usage: finalResponseData.usage, // Token Usage stats
                proposal: finalResponseData.proposal ? {
                    ...finalResponseData.proposal,
                    id: generateId(),
                    originalMessageId: loadingId,
                    status: 'active'
                } as any : undefined
            };

            // SINGLE PROPOSAL GUARD: If new proposal, clear any other active proposals and persist
            // Compute archived proposals BEFORE setState to avoid StrictMode double-invocation issues
            const messagesToArchive: Message[] = finalResponseData.proposal
                ? messages.filter(msg => msg.proposal && msg.proposal.status === 'active' && msg.id !== loadingId)
                    .map(msg => ({ ...msg, proposal: { ...msg.proposal!, status: 'completed' as const } }))
                : [];

            setMessages(prev => {
                return prev.map(msg => {
                    if (msg.id === loadingId) return finalMsg;
                    // Clear previous active proposals when new one arrives
                    if (finalResponseData?.proposal && msg.proposal && msg.proposal.status === 'active') {
                        return { ...msg, proposal: { ...msg.proposal, status: 'completed' as const } };
                    }
                    return msg;
                });
            });

            // Persist new message
            await dbService.saveMessage(currentUserId, finalMsg);

            // Persist archived proposals (computed before setState)
            for (const archivedMsg of messagesToArchive) {
                await dbService.saveMessage(currentUserId, archivedMsg);
            }

        } catch (error: any) {
            console.error("[AI Error]", error?.message || error, error);
            const errorString = (JSON.stringify(error) + (error?.message || '')).toLowerCase();

            // Detect specific error types
            const isQuotaError = error?.status === 429 ||
                error?.code === 'RESOURCE_EXHAUSTED' ||
                errorString.includes('429') ||
                errorString.includes('resource_exhausted') || errorString.includes('resource exhausted') ||
                errorString.includes('quota') || errorString.includes('rate limit');

            const isOverloadError = error?.status === 503 ||
                error?.code === 'UNAVAILABLE' ||
                errorString.includes('503') ||
                errorString.includes('overloaded') || errorString.includes('unavailable');

            // Both quota and overload errors are retryable
            const isTempError = error?._wasRetryable || isQuotaError || isOverloadError;

            // Friendly error messages - distinguish between quota and overload
            const errorText = errorString.includes('api key') || errorString.includes('api_key')
                ? "Authorization failed. Please check your API key in Settings."
                : isQuotaError
                    ? "API quota exceeded. Please try again later."
                    : isOverloadError
                        ? "The AI is busily thinking. Please wait a moment and try again."
                        : `Error: ${error?.message || "Something went wrong."}`;

            const errorMsg: Message = {
                ...loadingMsg,
                text: errorText,
                isLoading: false,
                suggestedActions: isTempError ? [{ label: 'Retry', message: text }] : undefined
            };
            setMessages(prev => prev.map(msg => msg.id === loadingId ? errorMsg : msg));
            await dbService.saveMessage(currentUserId, errorMsg);

            // Only show toast for non-retryable errors (not 503/429/quota)
            if (!isTempError) {
                showToast('AI Error', error?.message || 'Failed to generate response.', 'error');
            }

        } finally {
            setIsProcessing(false);
        }
    };

    // --- Live Mode Actions ---
    const handleSaveLiveMessage = async (role: MessageRole, text: string, groundingSources?: GroundingSource[]) => {
        if (!text.trim()) return;
        const currentUserId = user ? user.id : 'guest';

        const newMsg: Message = {
            id: generateId(),
            role,
            text,
            timestamp: Date.now(),
            ideaId: activeIdeaId,
            groundingSources // Pass grounding sources if available
        };

        setMessages(prev => [...prev, newMsg]);
        await dbService.saveMessage(currentUserId, newMsg);
    };

    // --- Briefing Search (Voice -> Text Delegation) ---
    const handleSaveBriefing = async (query: string, text: string, sources: GroundingSource[]) => {
        const currentUserId = user ? user.id : 'guest';

        const briefingMsg: Message = {
            id: generateId(),
            role: MessageRole.MODEL,
            text,
            timestamp: Date.now(),
            ideaId: activeIdeaId,
            groundingSources: sources,
            isBriefing: true,
            briefingQuery: query
        };

        setMessages(prev => [...prev, briefingMsg]);
        await dbService.saveMessage(currentUserId, briefingMsg);
    };

    // --- Granular Proposal Actions ---

    const applyChange = async (change: ProposalChange, idea: Idea | null) => {
        if (change.action === 'create_section') {
            if (!idea) {
                // Must be creating a concept
                const title = change.title || "New Concept";
                const content = change.content || "";
                await handleCreateIdea(title, content);
                return;
            }
            const newSection: Section = {
                id: generateId(),
                title: change.title || "New Section",
                content: change.content || "",
                lastUpdated: Date.now(),
                parentId: change.parentId || null,
                order: (idea.sections?.length || 0) + 1
            };
            const updatedSections = [...(idea.sections || []), newSection];
            await handleUpdateIdea(idea.id, idea.content, updatedSections, idea.attachments);
        } else if (change.action === 'update_section' && idea) {
            const updatedSections = idea.sections?.map(s =>
                s.id === change.sectionId ? { ...s, title: change.title || s.title, content: change.content || s.content, lastUpdated: Date.now() } : s
            );
            await handleUpdateIdea(idea.id, idea.content, updatedSections, idea.attachments);
        } else if (change.action === 'delete_section' && idea) {
            const toDelete = [change.sectionId, ...idea.sections!.filter(s => s.parentId === change.sectionId).map(s => s.id)];
            const updatedSections = idea.sections?.filter(s => !toDelete.includes(s.id));
            await handleUpdateIdea(idea.id, idea.content, updatedSections, idea.attachments);
        } else if (change.action === 'update_concept_title' && idea) {
            await handleUpdateIdea(idea.id, idea.content, idea.sections, idea.attachments, change.title);
        } else if (change.action === 'update_concept_overview' && idea) {
            await handleUpdateIdea(idea.id, change.content || "", idea.sections, idea.attachments);
        }
    };

    const handleAcceptProposalChange = async (messageId: string, change: ProposalChange) => {
        const msg = messages.find(m => m.id === messageId);
        if (!msg || !msg.proposal) return;

        // Find current idea state again to ensure freshness
        const currentIdea = ideas.find(i => i.id === activeIdeaId) || null;
        await applyChange(change, currentIdea);

        setMessages(prev => {
            return prev.map(m => {
                if (m.id === messageId && m.proposal) {
                    const newChanges = m.proposal.changes.map(c =>
                        c.id === change.id ? { ...c, status: 'accepted' as const } : c
                    );
                    const allDone = newChanges.every(c => c.status !== 'pending');
                    const updatedMsg = {
                        ...m,
                        proposal: {
                            ...m.proposal,
                            changes: newChanges,
                            status: allDone ? 'completed' : 'active' as any
                        }
                    };
                    dbService.saveMessage(user ? user.id : 'guest', updatedMsg);
                    return updatedMsg;
                }
                return m;
            });
        });
    };

    const handleRejectProposalChange = async (messageId: string, changeId: string) => {
        setMessages(prev => {
            return prev.map(m => {
                if (m.id === messageId && m.proposal) {
                    const newChanges = m.proposal.changes.map(c =>
                        c.id === changeId ? { ...c, status: 'rejected' as const } : c
                    );
                    const allDone = newChanges.every(c => c.status !== 'pending');
                    const updatedMsg = {
                        ...m,
                        proposal: {
                            ...m.proposal,
                            changes: newChanges,
                            status: allDone ? 'completed' : 'active' as any
                        }
                    };
                    dbService.saveMessage(user ? user.id : 'guest', updatedMsg);
                    return updatedMsg;
                }
                return m;
            });
        });
    };

    const handleAcceptAllChanges = async (messageId: string, changes: ProposalChange[]) => {
        if (changes.length === 0) return;

        let currentIdea = ideas.find(i => i.id === activeIdeaId) || null;

        if (currentIdea) {
            let updatedSections = [...(currentIdea.sections || [])];
            let updatedTitle = currentIdea.title;
            let updatedContent = currentIdea.content;

            for (const change of changes) {
                if (change.action === 'create_section') {
                    updatedSections.push({
                        id: generateId(),
                        title: change.title || "New Section",
                        content: change.content || "",
                        lastUpdated: Date.now(),
                        parentId: change.parentId || null,
                        order: updatedSections.length + 1
                    });
                } else if (change.action === 'update_section') {
                    updatedSections = updatedSections.map(s =>
                        s.id === change.sectionId ? { ...s, title: change.title || s.title, content: change.content || s.content, lastUpdated: Date.now() } : s
                    );
                } else if (change.action === 'delete_section') {
                    const toDelete = [change.sectionId, ...updatedSections.filter(s => s.parentId === change.sectionId).map(s => s.id)];
                    updatedSections = updatedSections.filter(s => !toDelete.includes(s.id as string));
                } else if (change.action === 'update_concept_title') {
                    updatedTitle = change.title || updatedTitle;
                } else if (change.action === 'update_concept_overview') {
                    updatedContent = change.content || updatedContent;
                }
            }

            // Single Commit to prevent race conditions
            // Pass through sanitizeSections for safety
            await handleUpdateIdea(currentIdea.id, updatedContent, sanitizeSections(updatedSections), currentIdea.attachments, updatedTitle);
        } else {
            // Creating concept (usually one change, but if multiple, handle first one as create)
            const createChange = changes.find(c => c.action === 'create_section' || c.action.includes('concept'));
            if (createChange) {
                // Logic for new concept
                const msg = messages.find(m => m.id === messageId);
                const title = msg?.proposal?.title || "New Concept";
                await handleCreateIdea(title, createChange.content);
            }
        }

        // Update Messages State to 'accepted'
        setMessages(prev => prev.map(m => {
            if (m.id === messageId && m.proposal) {
                const newChanges = m.proposal.changes.map(c =>
                    changes.find(ch => ch.id === c.id) ? { ...c, status: 'accepted' as const } : c
                );
                return {
                    ...m,
                    proposal: { ...m.proposal, changes: newChanges, status: 'completed' }
                };
            }
            return m;
        }));

        showToast('Changes Applied', 'All proposed updates have been merged.', 'success');

        // --- AUTO-FOLLOWUP: Trigger AI to continue planning ---
        setTimeout(() => {
            handleSendMessage("Changes approved and applied. ACTION REQUIRED: Mark these steps as completed in the plan using 'managePlan'. THEN, IMMEDIATELY generate and propose the next batch of changes based on the plan. Do not ask for details—generate them yourself based on the concept.");
        }, 500);
    };

    const handleRejectAllChanges = async (messageId: string, changes: ProposalChange[]) => {
        setMessages(prev => prev.map(m => {
            if (m.id === messageId && m.proposal) {
                const newChanges = m.proposal.changes.map(c =>
                    changes.find(ch => ch.id === c.id) ? { ...c, status: 'rejected' as const } : c
                );
                return {
                    ...m,
                    proposal: { ...m.proposal, changes: newChanges, status: 'completed' }
                };
            }
            return m;
        }));
        showToast('Rejected', 'Proposed changes were discarded.', 'info');
    };

    const handleRequestRevision = async (messageId: string, changeId: string, feedback: string) => {
        // 1. Mark item as revision requested
        const msg = messages.find(m => m.id === messageId);
        let changeTitle = "the proposed change";
        if (msg?.proposal) {
            const change = msg.proposal.changes.find(c => c.id === changeId);
            if (change) {
                changeTitle = change.title || change.action;
                setMessages(prev => prev.map(m => {
                    if (m.id === messageId && m.proposal) {
                        const newChanges = m.proposal.changes.map(c =>
                            c.id === changeId ? { ...c, status: 'revision_requested' as const } : c
                        );
                        const updatedMsg = { ...m, proposal: { ...m.proposal, changes: newChanges } };
                        dbService.saveMessage(user ? user.id : 'guest', updatedMsg);
                        return updatedMsg;
                    }
                    return m;
                }));
            }
        }

        // 2. Send feedback message to AI
        const prompt = `[SYSTEM: User requests revision for item "${changeTitle}". Feedback: "${feedback}". Please propose a new/updated change.]`;
        await handleSendMessage(prompt);
        showToast('Feedback Sent', 'The Architect will review your request.', 'info');
    };

    const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');

    return (
        <ErrorBoundary>
            <div className={`${theme}`}>
                <ToastContainer toasts={toasts} onDismiss={dismissToast} />
                <Suspense fallback={null}>
                    <SettingsDialog
                        isOpen={isSettingsOpen}
                        onClose={() => setIsSettingsOpen(false)}
                        theme={theme}
                        currentPaletteId={paletteId}
                        onPaletteChange={handlePaletteChange}
                        currentTextScale={textScale}
                        onTextScaleChange={handleTextScaleChange}
                        currentTextClarity={textClarity}
                        onTextClarityChange={handleTextClarityChange}
                        showTileEdgeColors={showTileEdgeColors}
                        onTileEdgeColorsChange={handleTileEdgeColorsChange}
                    />
                </Suspense>
                <Suspense fallback={null}>
                    {isDiagnosticOpen && <DiagnosticPanel onClose={() => setIsDiagnosticOpen(false)} theme={theme} />}
                </Suspense>

                <div className="fixed inset-0 flex flex-col overflow-hidden bg-cc-base text-cc-text transition-colors duration-300 safe-area-top">

                    {/* Offline Overlay */}
                    {!isOnline && user && (
                        <div className="absolute inset-0 z-[999] bg-white/80 dark:bg-slate-950/80 backdrop-blur-sm flex flex-col items-center justify-center p-6 animate-in fade-in">
                            <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl shadow-2xl text-center border border-red-100 dark:border-red-900/30 max-w-md">
                                <CloudOff size={48} className="mx-auto text-red-500 mb-4" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Connection Lost</h2>
                                <p className="text-gray-500 dark:text-gray-400 mb-6">
                                    Editing is paused to ensure data safety. All changes are saved to Google Drive, which requires an active internet connection.
                                </p>
                                <div className="flex items-center justify-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400 animate-pulse">
                                    <Wifi size={16} />
                                    Waiting for connection...
                                </div>
                            </div>
                        </div>
                    )}

                    {/* --- TOP HEADER BAR --- */}
                    <div className="h-9 min-h-[36px] bg-cc-elevated border-b border-cc-border flex items-center justify-between px-3 md:px-4 z-[60] flex-shrink-0">
                        <div className="flex items-center gap-2 md:gap-3">
                            <span className="text-[11px] font-semibold tracking-tight text-cc-text hidden sm:block">ConceptCanvas AI</span>
                            <span className="text-[11px] font-semibold tracking-tight text-cc-text sm:hidden">CC</span>

                            <div className="h-4 w-px bg-cc-border" />

                            <button
                                onClick={() => setIsArchitectMode(!isArchitectMode)}
                                className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold transition-all ${isArchitectMode
                                    ? 'bg-cc-toggle-active text-cc-toggle-active-text'
                                    : 'bg-cc-toggle-inactive text-cc-toggle-inactive-text'
                                    }`}
                                title={isArchitectMode ? "Switch to Explorer Mode (Research & Learning)" : "Switch to Architect Mode (Project Building)"}
                            >
                                {isArchitectMode ? <Component size={11} /> : <BookOpen size={11} />}
                                <span className="hidden sm:inline">{isArchitectMode ? 'Architect' : 'Explorer'}</span>
                                {isArchitectMode ? <ToggleRight size={12} /> : <ToggleLeft size={12} />}
                            </button>

                            <div className="relative">
                                <button
                                    onClick={() => setIsHelpOpen(!isHelpOpen)}
                                    className="p-1 text-cc-text-muted hover:text-cc-text transition-colors"
                                    title="Learn about ConceptCanvas AI"
                                >
                                    <HelpCircle size={14} />
                                </button>
                                {isHelpOpen && (
                                    <>
                                        <div className="fixed inset-0 z-[100]" onClick={() => setIsHelpOpen(false)} />
                                        <div className="fixed sm:absolute left-2 right-2 sm:left-0 sm:right-auto top-12 sm:top-full mt-0 sm:mt-2 w-auto sm:w-[380px] max-h-[80vh] overflow-y-auto bg-cc-elevated rounded-xl shadow-2xl border border-cc-border z-[101] animate-in fade-in slide-in-from-top-2 duration-200">
                                            <div className="sticky top-0 bg-cc-accent p-4 rounded-t-xl">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <Brain className="text-cc-accent-text" size={20} />
                                                        <h3 className="text-cc-accent-text font-bold text-sm">ConceptCanvas AI</h3>
                                                    </div>
                                                    <button onClick={() => setIsHelpOpen(false)} className="text-cc-accent-text opacity-70 hover:opacity-100">
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                                <p className="text-cc-accent-text opacity-80 text-xs mt-1">Your Symbiotic Workspace for Ideas</p>
                                            </div>

                                            <div className="p-4 space-y-4">
                                                <div className="bg-cc-accent-muted rounded-lg p-3 border border-cc-border">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Component className="text-cc-accent" size={16} />
                                                        <h4 className="font-bold text-sm text-cc-text">Architect Mode</h4>
                                                    </div>
                                                    <p className="text-xs text-cc-text-muted mb-2">
                                                        Build structured projects with AI-guided construction. The AI proposes organized changes as interactive cards you can accept, modify, or reject.
                                                    </p>
                                                    <ul className="text-xs text-cc-text-muted space-y-1">
                                                        <li className="flex items-start gap-1.5"><Sparkles size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" /> Create multi-section documents & reports</li>
                                                        <li className="flex items-start gap-1.5"><Lightbulb size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" /> AI suggests structure, content, and organization</li>
                                                        <li className="flex items-start gap-1.5"><FileText size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" /> Hierarchical sections with drag-and-drop</li>
                                                    </ul>
                                                </div>

                                                <div className="bg-cc-tile rounded-lg p-3 border border-cc-tile-border">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <BookOpen className="text-cc-accent" size={16} />
                                                        <h4 className="font-bold text-sm text-cc-text">Explorer Mode</h4>
                                                    </div>
                                                    <p className="text-xs text-cc-text-muted mb-2">
                                                        Research and learn with real-time web search. Uses Google Search grounding for current events, news, weather, and more.
                                                    </p>
                                                    <ul className="text-xs text-cc-text-muted space-y-1">
                                                        <li className="flex items-start gap-1.5"><Search size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" /> Live web search with source citations</li>
                                                        <li className="flex items-start gap-1.5"><Shield size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" /> Media literacy pedagogy built-in</li>
                                                        <li className="flex items-start gap-1.5"><Lightbulb size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" /> Critical evaluation of sources</li>
                                                    </ul>
                                                </div>

                                                <div className="border-t border-cc-border pt-3">
                                                    <h4 className="font-bold text-xs text-cc-text mb-2 uppercase tracking-wide">Voice & Live Mode</h4>
                                                    <div className="bg-cc-surface rounded-lg p-3 border border-cc-border">
                                                        <div className="flex items-center gap-1.5 mb-2">
                                                            <Mic className="text-cc-accent" size={14} />
                                                            <span className="text-xs font-semibold text-cc-text">Real-Time Voice Conversations</span>
                                                        </div>
                                                        <p className="text-[10px] text-cc-text-muted mb-2">
                                                            Talk naturally with AI using Gemini Live API. Features include:
                                                        </p>
                                                        <ul className="text-[10px] text-cc-text-muted space-y-1">
                                                            <li>• Multiple AI voices and avatar styles</li>
                                                            <li>• Live audio visualization with speech detection</li>
                                                            <li>• Floating HUD or controls bar interface options</li>
                                                            <li>• Works in both Architect and Explorer modes</li>
                                                        </ul>
                                                    </div>
                                                </div>

                                                <div className="border-t border-cc-border pt-3">
                                                    <h4 className="font-bold text-xs text-cc-text mb-2 uppercase tracking-wide">Export Suite</h4>
                                                    <p className="text-[10px] text-cc-text-muted mb-2">
                                                        Export your projects to professional documents. Choose specific sections to include with the selective export feature.
                                                    </p>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className="bg-cc-surface rounded-lg p-2 border border-cc-border">
                                                            <div className="flex items-center gap-1.5 mb-1">
                                                                <FileText className="text-cc-accent" size={12} />
                                                                <span className="text-[10px] font-semibold text-cc-text">Word/PDF</span>
                                                            </div>
                                                            <p className="text-[9px] text-cc-text-muted">Formatted documents with table of contents</p>
                                                        </div>
                                                        <div className="bg-cc-surface rounded-lg p-2 border border-cc-border">
                                                            <div className="flex items-center gap-1.5 mb-1">
                                                                <Presentation className="text-cc-accent" size={12} />
                                                                <span className="text-[10px] font-semibold text-cc-text">PowerPoint</span>
                                                            </div>
                                                            <p className="text-[9px] text-cc-text-muted">AI-generated slides with speaker notes</p>
                                                        </div>
                                                        <div className="bg-cc-surface rounded-lg p-2 border border-cc-border">
                                                            <div className="flex items-center gap-1.5 mb-1">
                                                                <Table className="text-cc-accent" size={12} />
                                                                <span className="text-[10px] font-semibold text-cc-text">Excel</span>
                                                            </div>
                                                            <p className="text-[9px] text-cc-text-muted">Structured data in spreadsheet format</p>
                                                        </div>
                                                        <div className="bg-cc-surface rounded-lg p-2 border border-cc-border">
                                                            <div className="flex items-center gap-1.5 mb-1">
                                                                <Sparkles className="text-cc-accent" size={12} />
                                                                <span className="text-[10px] font-semibold text-cc-text">AI Images</span>
                                                            </div>
                                                            <p className="text-[9px] text-cc-text-muted">Generate concept visuals from prompts</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="border-t border-cc-border pt-3">
                                                    <h4 className="font-bold text-xs text-cc-text mb-2 uppercase tracking-wide">Project Tile Icons</h4>
                                                    <div className="space-y-2">
                                                        <div className="flex items-start gap-2 bg-cc-surface rounded-lg p-2 border border-cc-border">
                                                            <Cloud size={14} className="text-cc-accent mt-0.5 flex-shrink-0" />
                                                            <div>
                                                                <span className="text-[10px] font-semibold text-cc-text">Cloud Icon</span>
                                                                <p className="text-[9px] text-cc-text-muted">Project folder exists in Google Drive. Your project has been synced to the cloud.</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-start gap-2 bg-cc-surface rounded-lg p-2 border border-cc-border">
                                                            <Brain size={14} className="text-cc-accent mt-0.5 flex-shrink-0" />
                                                            <div>
                                                                <span className="text-[10px] font-semibold text-cc-text">Brain Icon</span>
                                                                <p className="text-[9px] text-cc-text-muted">Linked to Google Doc. Your content syncs as plain text for universal portability—feed it to any AI or tool. Use Export for formatted documents.</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-start gap-2 bg-cc-surface rounded-lg p-2 border border-cc-border">
                                                            <Edit3 size={14} className="text-cc-accent mt-0.5 flex-shrink-0" />
                                                            <div>
                                                                <span className="text-[10px] font-semibold text-cc-text">Edit Button</span>
                                                                <p className="text-[9px] text-cc-text-muted">Opens the project workspace for editing sections, adding attachments, and exporting.</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="border-t border-cc-border pt-3">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <FolderSync className="text-cc-accent" size={14} />
                                                        <h4 className="font-bold text-xs text-cc-text uppercase tracking-wide">Google Drive Sync</h4>
                                                    </div>
                                                    <p className="text-[10px] text-cc-text-muted mb-2">
                                                        When signed in, your projects are automatically synced to Google Drive with this structure:
                                                    </p>
                                                    <div className="bg-cc-surface rounded-lg p-2 border border-cc-border font-mono text-[9px] text-cc-text-muted">
                                                        <div>My Drive/</div>
                                                        <div className="pl-3">└─ ConceptCanvas AI/</div>
                                                        <div className="pl-6">└─ [Project Name]/</div>
                                                        <div className="pl-9">├─ project.doc <span className="text-cc-accent">(plain text backup)</span></div>
                                                        <div className="pl-9">├─ chat-history.doc <span className="text-cc-accent">(conversations)</span></div>
                                                        <div className="pl-9">└─ exports/ <span className="text-cc-accent">(formatted docs)</span></div>
                                                    </div>
                                                    <p className="text-[9px] text-cc-text-subtle mt-2 italic">
                                                        The linked doc stays as simple text so you can feed it to any AI, converter, or tool. Use Export for polished Word, PowerPoint, or Excel files. All data stays in YOUR Google Drive.
                                                    </p>
                                                </div>

                                                <div className="border-t border-cc-border pt-3">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Shield className="text-cc-accent" size={14} />
                                                        <h4 className="font-bold text-xs text-cc-text uppercase tracking-wide">Data Sovereignty</h4>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <div className="bg-cc-surface rounded-lg p-2 border border-cc-border">
                                                            <span className="text-[10px] font-semibold text-cc-text">Guest Mode</span>
                                                            <p className="text-[9px] text-cc-text-muted">Data stored locally in your browser (localStorage/IndexedDB). Works offline, but data won't sync across devices. Export your projects to .ccai files for backup.</p>
                                                        </div>
                                                        <div className="bg-cc-surface rounded-lg p-2 border border-cc-border">
                                                            <span className="text-[10px] font-semibold text-cc-text">Cloud Mode</span>
                                                            <p className="text-[9px] text-cc-text-muted">Sync to your personal Google Drive. Access projects from any device. You maintain full control and can delete anytime.</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="border-t border-cc-border pt-3">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Users className="text-cc-accent" size={14} />
                                                        <h4 className="font-bold text-xs text-cc-text uppercase tracking-wide">Multi-Account Support</h4>
                                                    </div>
                                                    <p className="text-[10px] text-cc-text-muted mb-2">
                                                        Manage multiple Google accounts plus a dedicated Guest workspace. Each account has isolated data and projects.
                                                    </p>
                                                    <ul className="text-[10px] text-cc-text-muted space-y-1">
                                                        <li className="flex items-start gap-1.5"><Sparkles size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" /> Switch between accounts instantly</li>
                                                        <li className="flex items-start gap-1.5"><Shield size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" /> Complete data isolation between accounts</li>
                                                        <li className="flex items-start gap-1.5"><HardDrive size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" /> Guest workspace always available for local-only work</li>
                                                    </ul>
                                                </div>

                                                <div className="border-t border-cc-border pt-3">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <RefreshCw className="text-cc-accent" size={14} />
                                                        <h4 className="font-bold text-xs text-cc-text uppercase tracking-wide">Offline & Sync</h4>
                                                    </div>
                                                    <p className="text-[10px] text-cc-text-muted mb-2">
                                                        Work offline and sync when connected. Intelligent conflict resolution handles simultaneous edits.
                                                    </p>
                                                    <ul className="text-[10px] text-cc-text-muted space-y-1">
                                                        <li className="flex items-start gap-1.5"><Cloud size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" /> Auto-sync when connection restored</li>
                                                        <li className="flex items-start gap-1.5"><Lightbulb size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" /> Divergence detection with smart merge</li>
                                                        <li className="flex items-start gap-1.5"><FileDown size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" /> Export/import .ccai files for portability</li>
                                                    </ul>
                                                </div>

                                                <div className="border-t border-cc-border pt-3">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <FileDown className="text-cc-accent" size={14} />
                                                        <h4 className="font-bold text-xs text-cc-text uppercase tracking-wide">Import Format Guide</h4>
                                                    </div>
                                                    <p className="text-[10px] text-cc-text-muted mb-2">
                                                        Import existing projects using simple Markdown formatting.
                                                    </p>
                                                    <div className="bg-cc-surface rounded-lg p-2 border border-cc-border space-y-1.5 font-mono text-[9px] text-cc-text-muted">
                                                        <div># Project Title</div>
                                                        <div>## Section Title</div>
                                                        <div>### Subsection Title</div>
                                                        <div>#### Sub-subsection Title</div>
                                                        <div className="pt-1 text-cc-text-subtle">// Links become attachments</div>
                                                        <div>[Link Name](https://example.com)</div>
                                                    </div>
                                                </div>

                                                <div className="border-t border-cc-border pt-3">
                                                    <h4 className="font-bold text-xs text-cc-text mb-2 uppercase tracking-wide">Customization</h4>
                                                    <ul className="text-[10px] text-cc-text-muted space-y-1">
                                                        <li className="flex items-start gap-1.5"><Sparkles size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" /> 16 curated color themes with light/dark modes</li>
                                                        <li className="flex items-start gap-1.5"><FileText size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" /> 5-level text clarity system for readability</li>
                                                        <li className="flex items-start gap-1.5"><Lightbulb size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" /> Adjustable text scaling for accessibility</li>
                                                        <li className="flex items-start gap-1.5"><Component size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" /> 3D carousel or flat tile view modes</li>
                                                    </ul>
                                                </div>

                                                <div className="border-t border-cc-border pt-3">
                                                    <h4 className="font-bold text-xs text-cc-text mb-2 uppercase tracking-wide">Performance</h4>
                                                    <p className="text-[10px] text-cc-text-muted">
                                                        Optimized for smooth 120Hz displays with physics-based momentum scrolling. Touch-optimized for mobile with gesture support throughout the app.
                                                    </p>
                                                </div>

                                                <div className="border-t border-cc-border pt-3">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Smartphone className="text-cc-accent" size={14} />
                                                        <h4 className="font-bold text-xs text-cc-text uppercase tracking-wide">Phone as AI Workstation</h4>
                                                    </div>
                                                    <p className="text-[10px] text-cc-text-muted mb-2">
                                                        Run AI completely offline on your Android phone using Termux + Ollama. Zero API costs, full privacy.
                                                    </p>
                                                    <div className="bg-cc-surface rounded-lg p-2 border border-cc-border space-y-1.5">
                                                        <div className="flex items-start gap-1.5">
                                                            <Terminal size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" />
                                                            <span className="text-[9px] text-cc-text-muted"><strong>Setup:</strong> Install Termux from F-Droid, run <code className="bg-cc-bg px-1 rounded">pkg install tur-repo && pkg install ollama</code></span>
                                                        </div>
                                                        <div className="flex items-start gap-1.5">
                                                            <Server size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" />
                                                            <span className="text-[9px] text-cc-text-muted"><strong>Start server:</strong> <code className="bg-cc-bg px-1 rounded">ollama serve</code> (runs at localhost:11434)</span>
                                                        </div>
                                                        <div className="flex items-start gap-1.5">
                                                            <Zap size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" />
                                                            <span className="text-[9px] text-cc-text-muted"><strong>Best models:</strong> smollm:135m, gemma3:1b, llama3.2:1b, qwen2.5:1.5b</span>
                                                        </div>
                                                    </div>
                                                    <p className="text-[9px] text-cc-text-subtle mt-2 italic">
                                                        Dock your phone with Bluetooth keyboard/mouse for a portable AI workstation. Disable battery optimization for Termux in Settings.
                                                    </p>
                                                </div>

                                                <div className="border-t border-cc-border pt-3">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Server className="text-cc-accent" size={14} />
                                                        <h4 className="font-bold text-xs text-cc-text uppercase tracking-wide">Self-Hosted AI (Recommended)</h4>
                                                    </div>
                                                    <p className="text-[10px] text-cc-text-muted mb-2">
                                                        Run your own AI server for unlimited, free inference with GPT-4o level quality. No API costs, full privacy, works offline.
                                                    </p>
                                                    <div className="bg-cc-surface rounded-lg p-2 border border-cc-border space-y-1.5">
                                                        <div className="flex items-start gap-1.5">
                                                            <Zap size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" />
                                                            <span className="text-[9px] text-cc-text-muted"><strong>Thinking Models:</strong> Apriel 15B beats Gemini 2.5 Flash on benchmarks</span>
                                                        </div>
                                                        <div className="flex items-start gap-1.5">
                                                            <Search size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" />
                                                            <span className="text-[9px] text-cc-text-muted"><strong>SearXNG:</strong> Privacy-first web search grounding</span>
                                                        </div>
                                                        <div className="flex items-start gap-1.5">
                                                            <Mic size={10} className="mt-0.5 flex-shrink-0 text-cc-accent" />
                                                            <span className="text-[9px] text-cc-text-muted"><strong>Piper TTS:</strong> High-quality local voice synthesis</span>
                                                        </div>
                                                    </div>
                                                    <p className="text-[9px] text-cc-text-subtle mt-2 italic">
                                                        See SELF-HOSTED.md for setup. One-click scripts included for Windows, Mac, and Linux.
                                                    </p>
                                                </div>

                                                <div className="bg-cc-accent-muted rounded-lg p-3 border border-cc-border">
                                                    <p className="text-[10px] text-cc-text-muted text-center italic">
                                                        Self-Hosted Server • Google Gemini • Ollama • Groq • OpenRouter • LM Studio
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            <span
                                className="text-[11px] font-bold text-cc-accent bg-cc-accent/10 px-2 py-0.5 rounded"
                                title={`Provider: ${getActiveProviderInfo().name} | Model: ${getActiveProviderInfo().model}`}
                            >
                                {(() => {
                                    const providerInfo = getActiveProviderInfo();
                                    if (providerInfo.type === 'gemini') {
                                        const model = providerInfo.model.toLowerCase();
                                        const versionMatch = model.match(/gemini-(\d+\.?\d*)/);
                                        const version = versionMatch ? versionMatch[1] : '2.5';
                                        const variant = model.includes('pro') ? 'Pro' : 'Flash';
                                        return `Gemini ${version} ${variant}`;
                                    }
                                    return `${providerInfo.name}`;
                                })()}
                            </span>
                        </div>

                        <div className="flex items-center gap-2 md:gap-3">
                            {(() => {
                                // Check if there's an active Google account (not guest)
                                const activeAccount = accountService.getActiveAccount();
                                const isGoogleAccount = activeAccount && !activeAccount.isGuest;
                                const isCloudConnected = dbService.isCloudMode();

                                if (isGoogleAccount && !isCloudConnected) {
                                    // Google account active but Drive DB not loaded - disconnected state
                                    return (
                                        <SyncStatusIndicator
                                            isCloudMode={false}
                                            isDisconnected={true}
                                            onRetryConnection={async () => {
                                                if (activeAccount?.user) {
                                                    showToast('Reconnecting...', 'Attempting to reconnect to Google Drive', 'info');
                                                    try {
                                                        // Actually load from Drive (this sets dbFileId)
                                                        const data = await dbService.loadDriveData();
                                                        // Update UI with loaded data
                                                        const sanitizedIdeas = data.ideas.map((i: Idea) => ({
                                                            ...i,
                                                            sections: sanitizeSections(i.sections)
                                                        }));
                                                        setIdeas(sanitizedIdeas);
                                                        setMessages(data.messages);
                                                        // Set user to trigger re-render of this component
                                                        setUser(activeAccount.user);
                                                        showToast('Connected', 'Successfully reconnected to Google Drive', 'success');
                                                    } catch (e) {
                                                        showToast('Connection Failed', 'Could not connect to Google Drive. Please try signing out and back in.', 'error');
                                                    }
                                                }
                                            }}
                                        />
                                    );
                                } else if (isCloudConnected) {
                                    // Fully connected to Drive
                                    return <SyncStatusIndicator isCloudMode={true} />;
                                } else {
                                    // Guest/local mode
                                    return (
                                        <span className="hidden md:flex items-center gap-1 text-[9px] bg-cc-accent/10 text-cc-accent px-1.5 py-0.5 rounded-full border border-cc-accent/20" title="Saved locally on this device">
                                            <HardDrive size={9} /> Local
                                        </span>
                                    );
                                }
                            })()}

                            <div className="flex items-center gap-1">
                                {isDebugUserAllowed(user?.email) && (
                                    <button onClick={() => setIsDiagnosticOpen(true)} className="p-1 text-cc-text-muted hover:text-cc-text transition-colors" title="Diagnostics">
                                        <Bug size={11} />
                                    </button>
                                )}
                                <button onClick={() => setIsSettingsOpen(true)} className="p-1 text-cc-text-muted hover:text-cc-text transition-colors" title="Settings">
                                    <Settings size={11} />
                                </button>
                                <button onClick={toggleTheme} className="p-1 text-cc-text-muted hover:text-cc-text transition-colors">
                                    {theme === 'light' ? <Moon size={11} /> : <Sun size={11} />}
                                </button>
                                {/* ONNX Badge */}
                                <div className="scale-90 origin-left ml-2">
                                    <Suspense fallback={<div className="text-[10px] text-red-500 animate-pulse px-2">Loading...</div>}>
                                        <AIEdgeManager />
                                    </Suspense>
                                </div>
                            </div>

                            <div className="h-4 w-px bg-cc-border" />

                            <AccountSwitcher
                                currentUser={user}
                                onLogin={handleLogin}
                                onLogout={handleLogout}
                                onSwitchAccount={handleSwitchAccount}
                                isLoggingIn={isLoggingIn}
                            />
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="flex-1 flex flex-col overflow-hidden relative min-h-0">

                        {/* Top: Idea Manager (Workspace List) */}
                        <div className="flex-shrink-0 z-50 border-b border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                            <IdeaManager
                                ideas={ideas}
                                activeIdeaId={activeIdeaId}
                                onSelectIdea={handleSelectIdea}
                                onCreateIdea={handleCreateIdea}
                                onUpdateIdea={handleUpdateIdea}
                                onDeleteIdea={handleDeleteIdea}
                                theme={theme}
                                showToast={showToast}
                                isCloudMode={dbService.isCloudMode()}
                                getProjectDrivePath={dbService.getProjectDrivePath}
                                showTileEdgeColors={showTileEdgeColors}
                                // Chat Integration Props
                                chatProps={{
                                    messages,
                                    activeIdea,
                                    currentPlan,
                                    onSendMessage: handleSendMessage,
                                    onAcceptProposalChange: handleAcceptProposalChange,
                                    onRejectProposalChange: handleRejectProposalChange,
                                    onRequestRevision: handleRequestRevision,
                                    onAcceptAllChanges: handleAcceptAllChanges,
                                    onRejectAllChanges: handleRejectAllChanges,
                                    isProcessing,
                                    onStartLiveMode: () => setIsLiveMode(true),
                                    onStartLocalVoiceMode: () => setIsLocalVoiceMode(true),
                                    isArchitectMode,
                                    setIsArchitectMode,
                                    creativityLevel,
                                    setCreativityLevel
                                }}
                            />
                        </div>

                        {/* Middle: Chat Interface */}
                        <div className={`z-10 flex flex-col min-w-0 min-h-0 overflow-hidden transition-all duration-300 ${isChatFullscreen ? 'fixed inset-0 z-[90] bg-cc-base' :
                            isChatCollapsed ? 'h-12 flex-shrink-0' : 'flex-1 relative'
                            }`}>
                            <ChatInterface
                                messages={messages}
                                activeIdea={activeIdea}
                                currentPlan={currentPlan}
                                onSendMessage={handleSendMessage}
                                onAcceptProposalChange={handleAcceptProposalChange}
                                onRejectProposalChange={handleRejectProposalChange}
                                onRequestRevision={handleRequestRevision}
                                onAcceptAllChanges={handleAcceptAllChanges}
                                onRejectAllChanges={handleRejectAllChanges}
                                onDeleteMessage={handleDeleteMessage}
                                isProcessing={isProcessing}
                                onStartLiveMode={() => setIsLiveMode(true)}
                                onStartLocalVoiceMode={() => setIsLocalVoiceMode(true)}
                                isArchitectMode={isArchitectMode}
                                setIsArchitectMode={setIsArchitectMode}
                                creativityLevel={creativityLevel}
                                setCreativityLevel={setCreativityLevel}
                                isChatFullscreen={isChatFullscreen}
                                setIsChatFullscreen={setIsChatFullscreen}
                                isChatCollapsed={isChatCollapsed}
                                setIsChatCollapsed={setIsChatCollapsed}
                                theme={theme}
                                activeProvider={activeProvider}
                                onToggleProvider={handleToggleProvider}
                            />
                        </div>
                    </div>

                    {/* Live Interface Modal - Cloud Voice (Gemini Live) */}
                    <Suspense fallback={<div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-white" /></div>}>
                        {isLiveMode && (
                            <LiveInterface
                                onClose={() => setIsLiveMode(false)}
                                model={config.gemini.liveModel}
                                ideas={ideas}
                                activeIdea={activeIdea}
                                currentPlan={currentPlan}
                                messages={messages}
                                onCreateIdea={handleCreateIdea}
                                onSelectIdea={handleSelectIdea}
                                onUpdateIdea={handleUpdateIdea}
                                onDeleteIdea={handleDeleteIdea}
                                onSaveMessage={handleSaveLiveMessage}
                                onSaveBriefing={handleSaveBriefing}
                                onPlanUpdate={(plan) => { setCurrentPlan(plan); savePlanToIdea(plan); }}
                                onAcceptProposalChange={handleAcceptProposalChange}
                                onRejectProposalChange={handleRejectProposalChange}
                                onRequestRevision={handleRequestRevision}
                                onAcceptAllChanges={handleAcceptAllChanges}
                                onRejectAllChanges={handleRejectAllChanges}
                                theme={theme}
                                isArchitectMode={isArchitectMode}
                                setIsArchitectMode={setIsArchitectMode}
                                showToast={showToast}
                            />
                        )}
                    </Suspense>

                    {/* Local Voice Interface Modal - Offline Voice (Ollama + Piper/Coqui) */}
                    <Suspense fallback={<div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-white" /></div>}>
                        {isLocalVoiceMode && (
                            <LocalVoiceLiveInterface
                                onClose={() => setIsLocalVoiceMode(false)}
                                ideas={ideas}
                                activeIdea={activeIdea}
                                messages={messages}
                                onSaveMessage={handleSaveLiveMessage}
                                onAcceptProposalChange={handleAcceptProposalChange}
                                onRejectProposalChange={handleRejectProposalChange}
                                onRequestRevision={handleRequestRevision}
                                onAcceptAllChanges={handleAcceptAllChanges}
                                onRejectAllChanges={handleRejectAllChanges}
                                theme={theme}
                                isArchitectMode={isArchitectMode}
                                setIsArchitectMode={setIsArchitectMode}
                                showToast={showToast}
                                onSwitchToCloudVoice={() => { setIsLocalVoiceMode(false); setIsLiveMode(true); }}
                            />
                        )}
                        {isSettingsOpen && (
                            <SettingsDialog
                                isOpen={isSettingsOpen}
                                onClose={() => setIsSettingsOpen(false)}
                                theme={theme}
                                currentPaletteId={paletteId}
                                onPaletteChange={setPaletteId}
                                currentTextScale={textScale}
                                onTextScaleChange={setTextScale}
                                currentTextClarity={textClarity}
                                onTextClarityChange={setTextClarity}
                                currentUiScale={uiScale}
                                onChangeUiScale={(scale) => {
                                    console.log('[System] UI Scale Change Request:', scale);
                                    setUiScale(scale);
                                }}
                                showTileEdgeColors={showTileEdgeColors}
                                onTileEdgeColorsChange={setShowTileEdgeColors}
                            />
                        )}
                    </Suspense>

                    <ToastContainer toasts={toasts} onDismiss={dismissToast} />
                </div>
            </div>
        </ErrorBoundary>
    );
};

export default App;